import React, { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import AppRoutes from "./Routes";
import '@fortawesome/fontawesome-free/css/all.min.css';
import Footer from "./components/footer/Footer";
import NavigationBar from "./components/navigation/NavigationBar";
import { getLoginUrlWithRedirect } from "./constants/Constants";
import { getAuthEnv, rememberAuthEnv } from "./constants/authEnvs";
import {
  cookiesCleanup,
  getJwtToken,
  getRefreshToken,
  saveRefreshToken,
  saveUserInfo,
  saveUserToken,
} from "./utils/AuthUtils";
import { ssoRefreshToken } from "./services/AuthService";
import { UserProvider } from "./context/UserContext";
import { encodeUserInfo } from "./utils/userInfoEncoding";

// Everything inside <Router>: reads the shared-domain JWT cookie and gates the
// whole site. No token -> redirect to the auth portal the user signed in at
// (SID-2662).
function AuthenticatedApp() {
  const authEnv = getAuthEnv();
  const jwtToken = getJwtToken(authEnv);

  useEffect(() => {
    getAccessTokenSilently();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAccessTokenSilently = async () => {
    rememberAuthEnv(authEnv.id);
    if (jwtToken) {
      return; // token already present
    }

    const refreshToken = getRefreshToken(authEnv);
    if (!refreshToken) {
      cookiesCleanup(authEnv);
      window.location.href = getLoginUrlWithRedirect(authEnv);
      return;
    }

    try {
      const data = await ssoRefreshToken({ refreshToken }, authEnv);
      if (data?.status === 0) {
        saveUserToken(data?.accessToken, data?.expiresAt, authEnv);
        saveRefreshToken(data?.refreshToken, data?.refreshTokenExpiresAt, authEnv);

        const infoPayload = {
          applications: data?.applications,
          operatorId: data?.operatorId,
          name: data?.name,
          location: data?.location,
        };
        saveUserInfo(encodeUserInfo(infoPayload));
        window.location.reload();
      } else {
        cookiesCleanup(authEnv);
        window.location.href = getLoginUrlWithRedirect(authEnv);
      }
    } catch (err) {
      cookiesCleanup(authEnv);
      window.location.href = getLoginUrlWithRedirect(authEnv);
    }
  };

  if (!jwtToken) {
    // Portal redirect / silent refresh in flight — render nothing.
    return null;
  }

  return (
    <UserProvider>
      <NavigationBar />
      <AppRoutes />
      <Footer />
    </UserProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthenticatedApp />
    </Router>
  );
}

export default App;
